import { useCallback, useState } from "react";
import { api } from "@/lib/api";
import { buildGreeting, newMsg } from "./greetings";

const FALLBACK_REPLY =
    "Sorry, I couldn't reach the server. Please call 281.547.8447 or email info@crossroadstochange.com and our team will respond shortly.";

/**
 * Manages the chat transcript, session id, send/reset operations, and loading state.
 * Visitor identity is learned via the `learn(msg)` callback before each send so the
 * outgoing request carries the freshest name/role context.
 */
export default function useChatSession({ initialName, learn }) {
    const [messages, setMessages] = useState(() => [buildGreeting(initialName)]);
    const [sessionId, setSessionId] = useState(null);
    const [loading, setLoading] = useState(false);

    const send = useCallback(async (text) => {
        const msg = (text || "").trim();
        if (!msg) return;

        setMessages((prev) => [...prev, newMsg("user", msg)]);
        setLoading(true);

        const { name, role } = learn(msg);

        try {
            const { data } = await api.post("/chat", {
                session_id: sessionId,
                message: msg,
                user_name: name || undefined,
                user_role: role || undefined,
            });
            setSessionId(data.session_id);
            setMessages((prev) => [...prev, newMsg("assistant", data.reply)]);
        } catch (err) {
            console.warn("[chatbot] /chat request failed:", err);
            setMessages((prev) => [...prev, newMsg("assistant", FALLBACK_REPLY)]);
        } finally {
            setLoading(false);
        }
    }, [sessionId, learn]);

    const reset = useCallback((name) => {
        setMessages([buildGreeting(name)]);
        setSessionId(null);
    }, []);

    /** Replace the opening greeting if we've just learned the visitor's name from storage. */
    const rebuildGreetingWith = useCallback((name) => {
        setMessages((prev) => {
            if (prev.length === 1 && prev[0].role === "assistant") return [buildGreeting(name)];
            return prev;
        });
    }, []);

    /** Push a Jerry-side message into the transcript (e.g. handoff confirmation). */
    const appendSystem = useCallback((content) => {
        setMessages((prev) => [...prev, newMsg("assistant", content)]);
    }, []);

    return {
        messages,
        sessionId,
        loading,
        send,
        reset,
        rebuildGreetingWith,
        appendSystem,
    };
}
